import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink, useLocation, useParams, useNavigate } from 'react-router-dom';
import { Box, Grid, Stack, useMediaQuery, Link, Skeleton, Tabs, Tab, useTheme, Tooltip, IconButton, TextField, Typography } from '@mui/material';
import { client, ProductAttributeModel, Item, ItemDocument, AddOrderItemRequest, OrderItemDetails } from '../../../app/services/api/orderManagementClient';
import useLogError from '../../../hooks/useLogError';
import PageTitle from '../../../components/shared/PageTitle';
import PageArea from '../../../components/shared/PageArea';
import { Section } from '../../../components/ui/Section';
import CustomBreadCrumbs from '../../../components/ui/CustomBreadCrumbs';
import ProductIdentifiers from './components/ProductIdentifiers';
import ProductPricing from './components/ProductPricing';
import ProductAttributes from './components/ProductAttributes';
import ExportAttributes from './components/ExportAttributes';
import Documents from './components/Documents';
import { UserContext } from '../../../components/shared/useUser';
import { downloadFile } from './utils/ProductDocumentUtils';
import useNswagClient from '../../../hooks/api/useNswagClient';
import MessagesSnackbar from '../../../components/shared/MessagesSnackbar';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import { ProductDocumentModel } from '../../../app/services/api/ofdClient';
import { useActiveOrder } from '../../../contexts/ActiveOrderContext';

const enum ProductTabEnum {
  ProductIdentifiers = 1,
  ProductPricing = 2,
  ProductAttributes = 3,
  ExportAttributes = 4,
  Documents = 5
}

const ProductDetails = () => {
  const navigate = useNavigate();
  const { t } = useTranslation('productDetails');
  const { downloadProductDocument, uploadProductDocument, deleteProductDocument } = useNswagClient();
  const { logError } = useLogError();
  const [isLoading, setIsLoading] = useState(true);
  const { id } = useParams();
  const { setActiveOrder, setContainerData: setActiveContainerData } = useActiveOrder();

  const location = useLocation();
  console.log('ProductDetails location.state:', location.state);

  const catalogueId = location.state?.catalogueId;
  const catalogueName = location.state?.catalogueName;
  const orderId = location.state?.orderId;
  const quantity = location.state?.quantity || 0;
  const canChangeOrderQuantity = location.state?.canChangeOrderQuantity || false;
  const hasOrderContext = orderId !== undefined && orderId !== null;
  const fromOrderableItems = location.state?.fromOrderableItems || false;

  const [productDetails, setProductDetails] = useState<Item>();
  const [productDocuments, setProductDocuments] = useState<ItemDocument[]>([]);
  const [exportAttributes, setExportAttributes] = useState<ProductAttributeModel[]>([]);
  const [showAttributesSavedMessage, setShowAttributesSavedMessage] = useState(false);
  const [tabValue, setTabValue] = useState<ProductTabEnum>(ProductTabEnum.ProductIdentifiers);
  const { selectedStore, hasPermissionTo } = useContext(UserContext);
  const theme = useTheme();
  const [showDeleteMessage, setShowDeleteMessage] = useState(false);
  const [showUploadMessage, setShowUploadMessage] = useState(false);
  const isMobile = useMediaQuery(`(max-width:${theme.breakpoints.values.md}px)`);
  const [itemQuantity, setItemQuantity] = useState<number>(quantity);
  const [showUpdateQuantityMessage, setShowUpdateQuantityMessage] = useState(false);
  const [showDeleteItemMessage, setShowDeleteItemMessage] = useState(false);
  const [orderItemId, setOrderItemId] = useState<number | undefined>(location.state?.orderItemId);

  useEffect(() => {
    if (id) {
      loadData(id);
    }
  }, [id]);

  useEffect(() => {
    setItemQuantity(quantity);
  }, [quantity]);

  const loadData = (sku: string) => {
    setIsLoading(true);

    const itemDetailsPromise = client.getItemBySku(selectedStore?.storeNumber, sku, true)
      .then((response) => {
        setProductDetails(response.data);
      })
      .catch(logError)
      .finally(() => setIsLoading(false));

    const itemDocumentsPromise = client.getItemDocuments(selectedStore?.storeNumber, sku)
      .then((response) => {
        setProductDocuments(response.data);
      })
      .catch(logError)
      .finally(() => setIsLoading(false));

    const exportAttributesPromise = client.getItemAttributes(selectedStore?.storeNumber, sku)
      .then((response) => {
        setExportAttributes(response.data);
      })
      .catch(logError);

    Promise.all([itemDetailsPromise, itemDocumentsPromise, exportAttributesPromise])
      .finally(() => setIsLoading(false));
  };

  const handleSaveExportAttributes = (attributes: { attributeDefinitionId: number, value: string }[]) => {
    const payload = { sku: productDetails?.sku!, attributes: attributes };

    client.updateItemAttributes(selectedStore?.storeNumber, payload)
      .then((x) => {
        if (x.success) {
          setShowAttributesSavedMessage(true);
        }
        loadData(productDetails?.sku!);
      })
      .catch(logError);
  };

  const handleUploadFile = (
    documentId: number | undefined,
    documentToUpdateId: number | undefined,
    productId: number,
    file?: File,
    effectiveStartDate?: string,
    effectiveEndDate?: string,
    altName?: string,
  ) => {
    const buildPayloadAndUpload = (fileDataBase64: string | null, fileName?: string, fileType?: string) => {
      const payload: ProductDocumentModel = {
        id: documentId,
        documentId: documentToUpdateId,
        productId,
        fileName: fileName ?? undefined,
        fileType: fileType ?? undefined,
        fileDataBase64: fileDataBase64 ?? undefined,
        effectiveStartDate,
        effectiveEndDate,
        altDocName: altName,
      };

      uploadProductDocument(payload)
        .then(() => {
          loadData(productDetails?.sku!);
          setShowUploadMessage(true);
        })
        .catch(logError);
    };

    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        const fileData = reader.result
          ? reader.result.toString().replace(/^data:(.*,)?/, '')
          : '';
        buildPayloadAndUpload(fileData, file.name, file.type);
      };
      reader.readAsDataURL(file);
    } else {
      // No file selected — send metadata update only
      buildPayloadAndUpload(null);
    }
  };

  const handleDeleteOrderItem = async (orderId: number, orderItemId: number) => {
    if (!orderId || !orderItemId) return;
    try {
      const result = await client.deleteOrderItem(selectedStore?.storeNumber, orderId.toString(), orderItemId.toString());
      if (result.success) {
        setShowDeleteItemMessage(true);
        setItemQuantity(0);
        if (orderId) {
          try {
            const orderResponse = await client.getOrder(selectedStore?.storeNumber, orderId);
            if (orderResponse.success && orderResponse.data) {
              setActiveOrder(orderResponse.data);
            } const containerResponse = await client.getStoreContainerData(
              selectedStore?.storeNumber,
              orderId.toString(),
            );
            if (containerResponse.success && containerResponse.data) {
              setActiveContainerData(containerResponse.data);
            }
          } catch (error) {
            logError(error);
          }
        }
      }
    } catch (error) {
      logError(error);
    }
  };

  const handleDeleteFile = (documentId: number) => {
    deleteProductDocument(documentId)
      .then(() => {
        loadData(productDetails?.sku!);
        setShowDeleteMessage(true);
      })
      .catch(logError);
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => setTabValue(newValue);

  const handleQuantityChange = async (itemId: number, newQuantity: number) => {
    const request: AddOrderItemRequest = {
      orderId: orderId!,
      itemId,
      quantity: newQuantity,
      predictedQuantity: newQuantity,
    };

    const response = await client.addOrderItem(selectedStore?.storeNumber, request);
    if (response.success) {
      setItemQuantity(newQuantity);
      setShowUpdateQuantityMessage(true);
      if (orderId) {
        try {
          const orderResponse = await client.getOrder(selectedStore?.storeNumber, orderId);
          if (orderResponse.success && orderResponse.data) {
            setActiveOrder(orderResponse.data);
            console.log(itemId);
            const found = orderResponse.data.items.find(
              (itm: OrderItemDetails) => itm.itemId === itemId,
            );
            if (found) {
              setOrderItemId(found.id);
              console.log('Found order item ID:', found.id);
            }
          }

          const containerResponse = await client.getStoreContainerData(
            selectedStore?.storeNumber,
            orderId.toString(),
          );
          if (containerResponse.success && containerResponse.data) {
            setActiveContainerData(containerResponse.data);
          }
        } catch (error) {
          logError(error);
        }
      }
    }
  };

  return (
    <PageArea>
      {catalogueId && catalogueName && hasPermissionTo(['CataloguesView', 'CataloguesDetailView']) &&
        <Box>
          <CustomBreadCrumbs title={`${t('productDetails')} `}>
            {hasPermissionTo(['CataloguesView']) ?
              <Link to="/catalogue-list"
                component={RouterLink}>{t('catalogues')}</Link> : null}
            {hasPermissionTo(['CataloguesDetailView']) ?
              <Link to={`/ofd/catalogueproducts/${catalogueId}`}
                component={RouterLink}>{catalogueName}</Link> : null}
          </CustomBreadCrumbs>
        </Box>
      }
      {orderId && hasPermissionTo(['OrdersView']) &&
        <Box>
          <CustomBreadCrumbs title={`${t('productDetails')} `}>
            {hasPermissionTo(['OrdersView']) ?
              <Link to="/orders"
                component={RouterLink}>{t('orders')}</Link> : null}
            {hasPermissionTo(['OrdersView']) ?
              <Link to={`/order/${orderId}`}
                component={RouterLink}>{t('order') + ': ' + orderId}</Link> : null}
            {fromOrderableItems && hasPermissionTo(['OrdersView']) ?
              <Link to={`/products-list/${orderId}`}
                component={RouterLink}>{t('orderableItems')}</Link> : null}
          </CustomBreadCrumbs>
        </Box>
      }
      <Box
        display='flex'
        width='100%'
        height='100%'
        flexDirection='column'
      >
        <Stack width='100%'>
          <Box width={isMobile ? '100%' : 'auto'}
            display="flex"
            justifyContent="space-between">
            {isLoading ?
              <Skeleton variant="rounded"
                width={200}
                height={40} /> :
              <PageTitle>{t('product')} - {productDetails?.name}</PageTitle>
            }
            {hasOrderContext && (
              <Tooltip title="Go to basket"
                arrow>
                <IconButton aria-label="Go to basket"
                  onClick={() => navigate(`/order/${orderId}`)}
                  sx={{
                    backgroundColor: theme.palette.primary.main,
                    color: 'white',
                    '&:hover': {
                      backgroundColor: theme.palette.primary.dark,
                    },
                    p: 1.5,
                    borderRadius: '8px',
                  }}
                >
                  <ShoppingCartIcon />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </Stack>

        <Box position="relative"
          width="100%"
          height="200px"
          mb={3}>

          <Box
            height='200px'
            width='200px'
            border={`1px solid ${theme.palette.common.white}`}
            sx={{
              backgroundColor: (productDetails?.thumbnailUrl ?? '') != '' ? theme.palette.common.white : theme.palette.custom.gray[200],
              borderRadius: '5px',
              backgroundImage: `url(${productDetails?.thumbnailUrl ?? ''})`,
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat',
              backgroundSize: 'contain',
              imageRendering: '-webkit-optimize-contrast',
              flexShrink: 0,
              flexGrow: 0,
            }}
          >
          </Box>
          {hasOrderContext && (
            <Box sx={{
              position: 'absolute',
              left: '50%',
              transform: 'translateX(-50%)',
              top: '50%',
              display: 'flex',
              alignItems: 'center',
              gap: 3,
            }}
            >
              <Typography
                sx={{ fontWeight: 500, fontSize: '15px', mb: 0.5, color: 'text.secondary' }}
              >
                {t('quantity')}
              </Typography>
              <TextField
                size="small"
                type="number"
                autoComplete="off"
                inputProps={{ min: 0 }}
                disabled={isLoading || !canChangeOrderQuantity}
                value={itemQuantity}
                onFocus={e => e.target.select()}
                onChange={e => {
                  const value = e.target.value;
                  if (/^\d*$/.test(value)) {
                    setItemQuantity(Number(value));
                  }
                }}
                onBlur={async (e) => {
                  let value = Math.abs(parseInt((e.target as HTMLInputElement).value || '0'));
                  if (isNaN(value) || value < 0) value = 0;
                  if (value == 0) {
                    await handleDeleteOrderItem(orderId!, orderItemId ?? 0);
                  } else {
                    await handleQuantityChange(productDetails!.id, value);
                  }
                }}
                sx={{
                  width: '100px',
                  input: {
                    textAlign: 'center',
                  },
                }}
              />
            </Box>
          )}
        </Box>

        <Box
          display='flex'
          flexDirection='column'
          flex={1}
        >
          <Grid container
            item
            xs={12}
            mb={3}>
            <Grid item
              xs={12}>
              <Tabs value={tabValue}
                variant={isMobile ? 'scrollable' : 'fullWidth'}
                onChange={handleTabChange}
              >
                <Tab label={t('productIdentifiers')}
                  value={ProductTabEnum.ProductIdentifiers} />
                <Tab label={t('productPricing')}
                  value={ProductTabEnum.ProductPricing} />
                <Tab label={t('productAttributes')}
                  value={ProductTabEnum.ProductAttributes} />
                <Tab label={t('exportAttributes')}
                  value={ProductTabEnum.ExportAttributes} />
                <Tab label={t('documents')}
                  value={ProductTabEnum.Documents} />
              </Tabs>
            </Grid>
          </Grid>
          <Section>
            {isLoading
              ?
              <Skeleton sx={{ bgcolor: theme.palette.custom.gray[100] }}
                variant='rectangular'
                height={210.89}
              />
              :
              <>
                {tabValue === ProductTabEnum.ProductIdentifiers &&
                  <ProductIdentifiers productId={productDetails?.sku}
                    morrisonsId={productDetails?.internalId}
                    barcode={productDetails?.barcode}
                    legacyCode={productDetails?.legacyProductCode} />
                }
                {tabValue === ProductTabEnum.ProductPricing && <ProductPricing productDetails={productDetails} />}
                {tabValue === ProductTabEnum.ProductAttributes &&
                  <ProductAttributes caseDimensions={productDetails?.caseDimensions}
                    orderCategory={productDetails?.orderCategory}
                    productCategory={productDetails?.productCategory}
                    vat={productDetails?.vatRate}
                    weight={productDetails?.weight}
                    casesPerPallet={productDetails?.casesPerPallet}
                    casesPerLayer={productDetails?.casesPerLayer}
                    minShippingLife={productDetails?.minShippingLife}
                    minShelfLife={productDetails?.minShelfLife}
                    singlePick={productDetails?.singlePick}
                    caseSize={productDetails?.caseSize} />}
                {tabValue === ProductTabEnum.ExportAttributes &&
                  <ExportAttributes
                    exportAttributes={exportAttributes}
                    isLoading={isLoading}
                    onSave={handleSaveExportAttributes} />}
                {tabValue === ProductTabEnum.Documents &&
                  <Documents productId={productDetails?.id!}
                    productDocuments={productDocuments}
                    download={(documentId: number) => downloadFile(documentId, downloadProductDocument)}
                    upload={(documentId: number, documentToUpdateId: number | undefined, productId: number, file: File, effectiveStartDate: string, effectiveEndDate: string, altName: string) => handleUploadFile(documentId, documentToUpdateId, productId, file, effectiveStartDate, effectiveEndDate, altName)}
                    delete={(documentId: number) => handleDeleteFile(documentId)}
                  />
                }
              </>}
          </Section>
        </Box>

        <MessagesSnackbar
          open={showAttributesSavedMessage}
          onClose={() => setShowAttributesSavedMessage(false)}
          message={t('exportAttributesSaved')}
          severity='success'
          duration={3000}
        />
        <MessagesSnackbar open={showDeleteMessage}
          onClose={() => setShowDeleteMessage(false)}
          message={t('documentDeleted')}
          severity='success'
          duration={3000} />

        <MessagesSnackbar open={showUploadMessage}
          onClose={() => setShowUploadMessage(false)}
          message={t('documentUploaded')}
          severity='success'
          duration={3000} />

        <MessagesSnackbar open={showUpdateQuantityMessage}
          onClose={() => setShowUpdateQuantityMessage(false)}
          message={t('itemUpdated')}
          severity='success'
          duration={3000} />

        <MessagesSnackbar open={showDeleteItemMessage}
          onClose={() => setShowDeleteItemMessage(false)}
          message={t('deleteSuccess')}
          severity='success'
          duration={3000} />
      </Box>
    </PageArea>
  );
};

export default ProductDetails;